import { apiClient } from './apiClient';
import { camelize } from './_caseTransform';
import type {
  ProjectEnvironment,
  ProjectEnvironmentCreate,
  ProjectEnvironmentUpdate,
  ProjectEnvironmentWithStats,
} from '../types/projectEnvironment';

const STORAGE_KEY = 'gizmodata-project-environments';

const getStored = (): ProjectEnvironment[] => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
};

const setStored = (pes: ProjectEnvironment[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pes));
};

const createMock = (data: ProjectEnvironmentCreate): ProjectEnvironment => ({
  id: crypto.randomUUID(),
  name: data.name,
  label: data.label,
  projectId: data.projectId,
  environmentId: data.environmentId,
  defaultCloudRegionId: data.defaultCloudRegionId,
  createdByUserId: 'current-user',
  createdAt: new Date().toISOString(),
  updatedByUserId: 'current-user',
  updatedAt: new Date().toISOString(),
});

/**
 * ProjectEnvironment is the join of Project × Environment and the actual
 * deployment target for DatabaseClusters, Databases, and Catalogs.
 *
 * Backend endpoints (per API `/project-environments/` router):
 *   GET    /project-environments/?project_id=&environment_id=
 *   GET    /project-environments/{id}
 *   POST   /project-environments/
 *   PATCH  /project-environments/{id}   (partial; project_id/environment_id immutable)
 *   DELETE /project-environments/{id}
 *   (expanded variants at `/project-environments-expanded/...`)
 */
export const projectEnvironmentService = {
  async list(filters?: { projectId?: string; environmentId?: string }): Promise<ProjectEnvironment[]> {
    let mock = getStored();
    if (filters?.projectId) mock = mock.filter(pe => pe.projectId === filters.projectId);
    if (filters?.environmentId) mock = mock.filter(pe => pe.environmentId === filters.environmentId);

    const qs = new URLSearchParams();
    if (filters?.projectId) qs.set('project_id', filters.projectId);
    if (filters?.environmentId) qs.set('environment_id', filters.environmentId);
    const query = qs.toString();
    const path = query ? `/project-environments/?${query}` : '/project-environments/';

    const response = camelize(await apiClient.getAll<ProjectEnvironment>(path, mock));
    return response.data;
  },

  async get(id: string): Promise<ProjectEnvironment | null> {
    const mock = getStored().find(pe => pe.id === id);
    const response = camelize(await apiClient.get<ProjectEnvironment>(`/project-environments/${id}`, mock));
    return response.data;
  },

  async getWithStats(id: string): Promise<ProjectEnvironmentWithStats | null> {
    const mock = getStored().find(pe => pe.id === id);
    const mockWithStats: ProjectEnvironmentWithStats | undefined = mock
      ? { ...mock, instanceCount: 0, runningClusterCount: 0, databaseCount: 0, catalogCount: 0 }
      : undefined;
    const response = camelize(await apiClient.get<ProjectEnvironmentWithStats>(
      `/project-environments/${id}?expand=stats`,
      mockWithStats,
    ));
    return response.data;
  },

  async create(data: ProjectEnvironmentCreate): Promise<ProjectEnvironment> {
    const mock = createMock(data);
    const response = camelize(await apiClient.post<ProjectEnvironment>('/project-environments/', data));
    if (response.source === 'mock') {
      const all = getStored();
      all.push(response.data);
      setStored(all);
    }
    return response.data;
  },

  async update(id: string, data: ProjectEnvironmentUpdate): Promise<ProjectEnvironment> {
    const all = getStored();
    const idx = all.findIndex(pe => pe.id === id);
    const { defaultCloudRegionId, ...rest } = data;
    const mockUpdated: ProjectEnvironment | undefined = idx >= 0
      ? {
          ...all[idx],
          ...rest,
          // null = clear the override; undefined = untouched (mirrors the API's PATCH semantics).
          defaultCloudRegionId: defaultCloudRegionId === undefined
            ? all[idx].defaultCloudRegionId
            : (defaultCloudRegionId ?? undefined),
          updatedAt: new Date().toISOString(),
        }
      : undefined;

    // PATCH with exclude_unset on the API: `default_cloud_region_id: null` clears the
    // override (null survives snakeKeysDeep + JSON.stringify), omitted fields are untouched.
    const response = camelize(await apiClient.patch<ProjectEnvironment>(`/project-environments/${id}`, data));
    if (response.source === 'mock' && idx >= 0) {
      all[idx] = response.data;
      setStored(all);
    }
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete<void>(`/project-environments/${id}`, undefined);
    const remaining = getStored().filter(pe => pe.id !== id);
    setStored(remaining);
  },

  /**
   * System admins only: purge the TERMINATED clusters this deployment target still holds as
   * history (DELETE /database-clusters/{id}?purge=true), the blocker that otherwise makes
   * DELETE /project-environments/{id} answer 409 forever. The API refuses (409) a cluster
   * terminated today (UTC) — its usage isn't finalized — and 403s non-system-admins; either
   * error propagates. Live clusters are left alone. Returns how many were purged.
   */
  async purgeTerminatedClusters(id: string): Promise<number> {
    const resp = camelize(await apiClient.getAll<{ id: string; terminatedAt?: string | null }>(
      `/database-clusters/?project_environment_id=${encodeURIComponent(id)}`,
    ));
    const terminated = (resp.data || []).filter(c => c.terminatedAt);
    for (const cluster of terminated) {
      await apiClient.delete<void>(`/database-clusters/${cluster.id}?purge=true`, undefined);
    }
    return terminated.length;
  },
};
